import type { CancellationPolicy } from './queries';

/**
 * Lo que la pantalla de cancelación sabe, ATADO a la reserva que lo produjo:
 * la consulta de política y el resultado del cancel.
 *
 * Existe porque la política es plata de UNA reserva y la pantalla cambia de
 * reserva sin desmontarse: el modal del flujo reemplaza la suya y el detalle
 * del listado recibe otra por props. Sin la identidad pegada al resultado,
 * una política traída para la reserva A puede quedar en pantalla —o llegar
 * tarde y pisarla— cuando el asesor ya está por cancelar la B, que es la
 * forma exacta de aceptar un cargo que no es el que se está aceptando.
 *
 * Por eso todo lo que se guarda lleva `bookingId`, y lo que se lee se lee
 * SIEMPRE para una reserva: lo que no es de ella no existe.
 */
export interface CancellationEnquiry {
  /** De qué reserva es esto. `null` = todavía no se preguntó por ninguna. */
  bookingId: string | null;
  policy: CancellationPolicy | null;
  /** Por qué no hay política (texto del backend o del proveedor). */
  notice: string | null;
  loading: boolean;
  /**
   * Ya se preguntó (o se decidió no preguntar porque el conector dice que no
   * publica). Sin esto no hay nada que afirmar: dibujar "no publica política"
   * sin haberla pedido sería inventar una respuesta que nadie dio.
   */
  asked: boolean;
}

export const NO_ENQUIRY: CancellationEnquiry = {
  bookingId: null,
  policy: null,
  notice: null,
  loading: false,
  asked: false,
};

/**
 * Lo que se sabe de ESTA reserva. De otra —o de ninguna— no se muestra nada:
 * la política de una reserva no describe a la siguiente.
 */
export function enquiryOf(
  state: CancellationEnquiry,
  bookingId: string | undefined,
): CancellationEnquiry {
  return bookingId && state.bookingId === bookingId ? state : NO_ENQUIRY;
}

/**
 * Empieza la consulta de una reserva. Descarta lo que hubiera de otra —no es
 * de esta— y marca que ya se preguntó. `enquire: false` (el conector dice que
 * el proveedor no publica política) pregunta a nadie: queda `asked` sin
 * carga, que es lo que hace que la pantalla diga que no hay política en vez
 * de esperar una respuesta que no viene.
 */
export function enquiryStarted(
  bookingId: string,
  enquire: boolean,
): CancellationEnquiry {
  return {
    bookingId,
    policy: null,
    notice: null,
    loading: enquire,
    asked: true,
  };
}

/**
 * Llegó la política. Se aplica SOLO si el estado sigue siendo el de esa
 * misma consulta: una respuesta que llega tarde, cuando la pantalla ya está
 * en otra reserva o ya arrancó otra consulta, se descarta.
 */
export function enquiryResolved(
  state: CancellationEnquiry,
  bookingId: string,
  policy: CancellationPolicy,
): CancellationEnquiry {
  if (state.bookingId !== bookingId || !state.loading) return state;
  return { ...state, policy, notice: null, loading: false };
}

/** Falló la consulta: mismo criterio de descarte que `enquiryResolved`. */
export function enquiryFailed(
  state: CancellationEnquiry,
  bookingId: string,
  notice: string,
): CancellationEnquiry {
  if (state.bookingId !== bookingId || !state.loading) return state;
  return { ...state, policy: null, notice, loading: false };
}

/**
 * ¿Hay que preguntar antes de ofrecer el botón rojo? Sí, salvo que ya se
 * tenga la política DE ESTA reserva: la de otra no sirve, y una consulta en
 * vuelo ya está en camino.
 */
export function needsEnquiry(
  state: CancellationEnquiry,
  bookingId: string,
): boolean {
  const own = enquiryOf(state, bookingId);
  return !own.policy && !own.loading;
}

/**
 * Abrir el diálogo de cancelación SIEMPRE refresca el costo puntual, incluso
 * si antes se precargó una política para decidir el confirm. Solo evita
 * duplicar una consulta que ya está en vuelo para esa misma reserva.
 */
export function shouldRefreshCancellationPolicyOnOpen(
  state: CancellationEnquiry,
  bookingId: string,
): boolean {
  return !enquiryOf(state, bookingId).loading;
}

/**
 * ¿El resultado de una operación pedida para `attemptBookingId` le pertenece
 * todavía a la pantalla?
 *
 * Vale para TODO lo que vuelve tarde, no solo para la política: el cancel de
 * una reserva puede resolverse (o fallar) después de que la pantalla ya está
 * en otra —las dos pantallas cambian de reserva sin desmontarse—, y aplicar
 * esa fila, cerrar el diálogo que el asesor acaba de abrir para OTRA reserva,
 * o dejarle el motivo de un rechazo ajeno, es mostrarle el resultado de una
 * operación irreversible sobre la reserva equivocada.
 *
 * Descartar la actualización local no pierde nada: la operación ya ocurrió
 * del lado del hub y el listado se invalida igual, así que la fila real se
 * vuelve a leer por el camino de siempre.
 */
export function outcomeAppliesToScreen(
  attemptBookingId: string,
  screenBookingId: string | undefined,
): boolean {
  return Boolean(screenBookingId) && attemptBookingId === screenBookingId;
}
